// src/pages/Contact/Contact.jsx - Updated version
import React, { useState } from "react";
import Layout from "../../components/Layout/Layout";
import { Mail, User, MessageSquare, Send, CheckCircle, AlertCircle } from "lucide-react";
import "./Contact.css";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setError("All fields are required");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_URL}/contact/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setFormData({ name: "", email: "", message: "" });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        throw new Error(data.detail || "Failed to send message");
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError("Cannot connect to server. Please check your internet connection.");
      } else {
        setError(err.message);
      }
      console.error("Contact form error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="contact-page">
        <div className="contact-card">
          <div className="contact-header">
            <Mail size={48} className="contact-icon" />
            <h2>Contact Us</h2>
            <p>We'd love to hear from you! Send us a message below:</p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="success-banner">
              <CheckCircle size={18} />
              <span>✅ Message sent successfully! We'll get back to you soon.</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="error-banner">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Name Field */}
            <div className="form-group">
              <label>Your Name</label>
              <div className="input-wrapper">
                <User size={20} className="input-icon" />
                <input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  className="with-icon"
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="form-group">
              <label>Your Email</label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  className="with-icon"
                />
              </div>
            </div>

            {/* Message Field */}
            <div className="form-group">
              <label>Your Message</label>
              <div className="input-wrapper">
                <MessageSquare size={20} className="input-icon textarea-icon" />
                <textarea
                  name="message"
                  placeholder="Tell us what's on your mind..."
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  disabled={loading}
                  required
                  className="with-icon"
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading || !formData.name || !formData.email || !formData.message}
              className={loading ? "loading" : ""}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Contact;